import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import AddCommentModal from './AddCommentModal'
import EditCommentModal from './EditCommentModal'
import EditThreadModal from './EditThreadModal'
import ReportModal from './ReportModal'
import { useSpeech } from '@/hooks/useSpeech'
import { useModerator } from '@/hooks/useModerator'

interface GroupCommentsProps {
  threadId: string | null
  groupId: string
}

interface Comment {
  id: string
  body: string
  created_at: string
  author_id: string | null
  profiles: {
    display_name: string
  } | null
}

interface Thread {
  id: string
  title: string
  body: string
  created_at: string
  author_id: string | null
  profiles: {
    display_name: string
  } | null
}

export default function GroupComments({ threadId }: GroupCommentsProps) {
  const [showAddCommentModal, setShowAddCommentModal] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null)
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null)
  const [reportingThreadId, setReportingThreadId] = useState<string | null>(null)
  const isModerator = useModerator()
  const queryClient = useQueryClient()
  const {
    speakMessages,
    pauseSpeaking,
    resumeSpeaking,
    stopSpeaking,
    isSpeaking,
    isPaused,
    isSupported,
  } = useSpeech()

  // Fetch selected thread details
  const { data: thread } = useQuery({
    queryKey: ['thread', threadId],
    queryFn: async () => {
      if (!threadId) return null
      const { data, error } = await supabase
        .from('threads')
        .select(`
          id,
          title,
          body,
          created_at,
          author_id,
          profiles (
            display_name
          )
        `)
        .eq('id', threadId)
        .single()

      if (error) throw error
      
      // Transform data: Supabase returns profiles as array, extract first element
      const transformed = {
        ...data,
        profiles: Array.isArray(data.profiles) 
          ? (data.profiles[0] || null)
          : data.profiles,
      } as Thread
      
      return transformed
    },
    enabled: !!threadId,
  })

  // Fetch comments for selected thread
  const { data: comments, isLoading } = useQuery({
    queryKey: ['thread-comments', threadId],
    queryFn: async () => {
      if (!threadId) return []
      const { data, error } = await supabase
        .from('comments')
        .select(`
          id,
          body,
          created_at,
          author_id,
          profiles (
            display_name
          )
        `)
        .eq('thread_id', threadId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: true })

      if (error) throw error
      
      // Transform data: Supabase returns profiles as array, extract first element
      const transformed = (data || []).map((comment: any) => ({
        ...comment,
        profiles: Array.isArray(comment.profiles)
          ? (comment.profiles[0] || null)
          : comment.profiles,
      })) as Comment[]
      
      return transformed
    },
    enabled: !!threadId,
  })

  const handleAddComment = () => {
    if (!threadId) return
    setShowAddCommentModal(true)
  }

  const handleCommentAdded = () => {
    setShowAddCommentModal(false)
    // Comments list will refresh automatically via query invalidation
  }

  const handleListen = () => {
    if (!comments || comments.length === 0) return

    // Build messages array: only comment text (no author names)
    const messages: Array<{ text: string; authorId: string }> = []

    // Add all comments (just the text, no author names)
    comments.forEach((comment) => {
      messages.push({
        text: comment.body,
        authorId: comment.author_id || 'comment-author',
      })
    })

    speakMessages(messages)
  }

  const handleStopListen = () => {
    stopSpeaking()
  }

  const handlePauseResume = () => {
    if (isPaused) {
      resumeSpeaking()
    } else {
      pauseSpeaking()
    }
  }

  const deleteThread = useMutation({
    mutationFn: async (threadIdToDelete: string) => {
      // First, delete all comments for this thread
      const { error: commentsError } = await supabase
        .from('comments')
        .delete()
        .eq('thread_id', threadIdToDelete)

      if (commentsError) throw commentsError

      // Then delete the thread itself
      const { error } = await supabase
        .from('threads')
        .delete()
        .eq('id', threadIdToDelete)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
    },
  })

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thread-comments', threadId] })
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
    },
  })

  const handleEditComment = (commentId: string) => {
    setEditingCommentId(commentId)
  }

  const handleDeleteComment = async (commentId: string) => {
    if (confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      try {
        await deleteComment.mutateAsync(commentId)
      } catch (err: any) {
        alert(err.message || 'Failed to delete comment')
      }
    }
  }

  // Cleanup speech on unmount or when thread changes
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
    }
  }, [threadId])

  return (
    <div>
      {!threadId ? (
        <>
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Comments
          </h2>
          <p style={{ color: 'var(--text-primary)' }}>
            Select a thread to view comments.
          </p>
        </>
      ) : (
        <div
          className="pl-2 -ml-2 border-l-4 rounded"
          style={{
            borderLeftColor: 'var(--color-focus)',
          }}
          role="region"
          aria-labelledby="comments-for-thread-heading"
        >
          <h2
            id="comments-for-thread-heading"
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            {thread ? `Comments for: ${thread.title}` : 'Comments'}
          </h2>

          {/* Selected Thread Card */}
          {thread && (
            <div
              className="p-4 rounded border-2 mb-4"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h3 
                    className="text-lg font-bold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {thread.title}
                  </h3>
                  <div 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    By {thread.profiles?.display_name || 'Unknown'}{' '}
                    {format(new Date(thread.created_at), 'dd/MM/yyyy, HH:mm:ss')}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReportingThreadId(thread.id)}
                    className="px-3 py-1 text-sm font-semibold rounded"
                    style={{
                      backgroundColor: 'var(--color-error)',
                      color: 'var(--text-inverse)',
                    }}
                    aria-label={`Report thread: ${thread.title}`}
                  >
                    Report
                  </button>
                  {isModerator && (
                    <>
                      <button
                        onClick={() => setEditingThreadId(thread.id)}
                        className="px-3 py-1 text-sm font-semibold rounded"
                        style={{
                          backgroundColor: 'var(--bg-interactive)',
                          color: 'var(--text-inverse)',
                        }}
                        aria-label={`Edit thread: ${thread.title}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm('Are you sure you want to delete this thread? This action cannot be undone.')) {
                            try {
                              await deleteThread.mutateAsync(thread.id)
                              // Clear thread selection after deletion
                              // Note: This will be handled by the parent component
                            } catch (err: any) {
                              alert(err.message || 'Failed to delete thread')
                            }
                          }
                        }}
                        className="px-3 py-1 text-sm font-semibold rounded"
                        style={{
                          backgroundColor: 'var(--color-error)',
                          color: 'var(--text-inverse)',
                        }}
                        aria-label={`Delete thread: ${thread.title}`}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={handleAddComment}
                  className="px-4 py-2 font-semibold rounded"
                  style={{
                    backgroundColor: 'var(--bg-interactive)',
                    color: 'var(--text-inverse)',
                  }}
                  aria-label="Add a comment to this thread"
                >
                  Add Comment
                </button>
                {isSupported ? (
                  <>
                    {!isSpeaking ? (
                      <button
                        onClick={handleListen}
                        className="px-4 py-2 font-semibold rounded"
                        style={{
                          backgroundColor: 'var(--bg-interactive)',
                          color: 'var(--text-inverse)',
                        }}
                        aria-label="Listen to thread and comments"
                      >
                        🔊 Listen
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handlePauseResume}
                          className="px-4 py-2 font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--bg-interactive)',
                            color: 'var(--text-inverse)',
                          }}
                          aria-label={isPaused ? 'Resume playback' : 'Pause playback'}
                        >
                          {isPaused ? '▶ Resume' : '⏸ Pause'}
                        </button>
                        <button
                          onClick={handleStopListen}
                          className="px-4 py-2 font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--color-error)',
                            color: 'var(--text-inverse)',
                          }}
                          aria-label="Stop playback"
                        >
                          ⏹ Stop
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <button
                    disabled
                    className="px-4 py-2 font-semibold rounded opacity-50 cursor-not-allowed"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                    }}
                    aria-label="Text-to-speech not supported in this browser"
                    title="Text-to-speech not supported in this browser"
                  >
                    🔊 Listen (Not Supported)
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Comments List */}
          {isLoading ? (
            <p style={{ color: 'var(--text-primary)' }}>Loading comments...</p>
          ) : !comments || comments.length === 0 ? (
            <p style={{ color: 'var(--text-primary)' }}>
              No comments yet. Be the first to comment!
            </p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => {
                const commentDate = format(
                  new Date(comment.created_at),
                  'dd/MM/yyyy'
                )
                const authorName = comment.profiles?.display_name || 'Unknown'

                return (
                  <div
                    key={comment.id}
                    className="p-4 rounded border-2"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p 
                          className="mb-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {comment.body}
                        </p>
                        <p 
                          className="text-sm"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          By {authorName} {commentDate}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setReportingCommentId(comment.id)}
                          className="px-3 py-1 text-sm font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--color-error)',
                            color: 'var(--text-inverse)',
                          }}
                          aria-label={`Report comment by ${authorName}`}
                        >
                          Report
                        </button>
                        {isModerator && (
                          <>
                            <button
                              onClick={() => handleEditComment(comment.id)}
                              className="px-3 py-1 text-sm font-semibold rounded"
                              style={{
                                backgroundColor: 'var(--bg-interactive)',
                                color: 'var(--text-inverse)',
                              }}
                              aria-label={`Edit comment by ${authorName}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="px-3 py-1 text-sm font-semibold rounded"
                              style={{
                                backgroundColor: 'var(--color-error)',
                                color: 'var(--text-inverse)',
                              }}
                              aria-label={`Delete comment by ${authorName}`}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {showAddCommentModal && threadId && (
            <AddCommentModal
              threadId={threadId}
              onClose={() => setShowAddCommentModal(false)}
              onSuccess={handleCommentAdded}
            />
          )}

          {editingCommentId && (() => {
            const comment = comments?.find(c => c.id === editingCommentId)
            if (!comment) return null
            return (
              <EditCommentModal
                commentId={comment.id}
                currentBody={comment.body}
                onClose={() => setEditingCommentId(null)}
                onSuccess={() => {
                  setEditingCommentId(null)
                }}
              />
            )
          })()}

          {editingThreadId && thread && (
            <EditThreadModal
              threadId={thread.id}
              currentTitle={thread.title}
              currentBody={thread.body}
              onClose={() => setEditingThreadId(null)}
              onSuccess={() => {
                setEditingThreadId(null)
                // Refresh thread data
                queryClient.invalidateQueries({ queryKey: ['thread', threadId] })
              }}
            />
          )}

          {reportingCommentId && (
            <ReportModal
              targetType="comment"
              targetId={reportingCommentId}
              onClose={() => setReportingCommentId(null)}
              onSuccess={() => {
                setReportingCommentId(null)
                alert('Report submitted successfully. Thank you for helping keep our community safe.')
              }}
            />
          )}

          {reportingThreadId && (
            <ReportModal
              targetType="thread"
              targetId={reportingThreadId}
              onClose={() => setReportingThreadId(null)}
              onSuccess={() => {
                setReportingThreadId(null)
                alert('Report submitted successfully. Thank you for helping keep our community safe.')
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

